import { useState, useRef, useEffect } from "react";
import "./CheckoutBranch.css";
import {
  MapPin as LucideMapPin,
  Calendar as LucideCalendar,
} from "lucide-react";
import FallbackImg from "../../components/FallbackImg";
import "react-calendar/dist/Calendar.css";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Calendar from "react-calendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomDropdown from "../../components/customdropdown_v2/CustomDropdown";
import ApiService from "../../components/Api_services";

const INITIAL_SP = {
  sp_name: "",
  sp_phone: "",
  sp_addr: "",
  sp_state: "",
  sp_city: "",
  sp_postcode: "",
};

const CheckoutBranch = ({
  branch,
  checkoutData,
  onClose,
  hasSalesmanId,
  onSpecialDateChange,
  specialDate,
  isSpecialDateEnabled,
  onToggleSpecialDate,
  hasSpecialPoint,
  isSpecialPointEnabled,
  onToggleSpecialPoint,
  onSpecialPointChange,
  specialPointData,
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);
  const modalRef = useRef(null);

  const [spForm, setSpForm] = useState(specialPointData || INITIAL_SP);
  const [spErrors, setSpErrors] = useState({});

  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [postcodeOptions, setPostcodeOptions] = useState([]);
  const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    };
    if (calendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [calendarOpen]);

  const handleToggle = () => {
    const next = !isSpecialDateEnabled;
    onToggleSpecialDate(branch.branch_code, next);
    if (!next) {
      setCalendarOpen(false);
      if (onSpecialDateChange) onSpecialDateChange(branch.branch_code, null);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    setSpForm(specialPointData || INITIAL_SP);
  }, [specialPointData]);

  const handleToggleSpecialPoint = () => {
    const next = !isSpecialPointEnabled;
    if (onToggleSpecialPoint) onToggleSpecialPoint(branch.branch_code, next);
    if (!next) {
      setSpForm(INITIAL_SP);
      setSpErrors({});
      if (onSpecialPointChange) onSpecialPointChange(branch.branch_code, null);
    }
  };

  const handleSpChange = (field, value) => {
    const updated = { ...spForm, [field]: value };
    setSpForm(updated);
    if (spErrors[field]) setSpErrors((e) => ({ ...e, [field]: "" }));

    if (onSpecialPointChange) onSpecialPointChange(branch.branch_code, updated);
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const states = await ApiService.getAllStates();
        setStateOptions(states.map((s) => ({ value: s, label: s })));
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!spForm.sp_state) return;
    const fetchCities = async () => {
      try {
        const cities = await ApiService.getCitiesByState(spForm.sp_state);
        setCityOptions(cities.map((c) => ({ value: c.city, label: c.city })));
      } catch (err) {
        console.error("Error restoring cities:", err);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    if (!spForm.sp_city) return;
    const fetchPostcodes = async () => {
      try {
        const postcodes = await ApiService.getPostcodeByCity(spForm.sp_city);
        setPostcodeOptions(postcodes.map((p) => ({ value: p, label: p })));
      } catch (err) {
        console.error("Error restoring postcodes:", err);
      }
    };
    fetchPostcodes();
  }, []);

  const handleStateChange = async (selected) => {
    const updated = {
      ...spForm,
      sp_state: selected ?? "",
      sp_city: "",
      sp_postcode: "",
    };
    setSpForm(updated);
    if (spErrors.sp_state) setSpErrors((e) => ({ ...e, sp_state: "" }));
    if (onSpecialPointChange) onSpecialPointChange(branch.branch_code, updated);

    setCityOptions([]);
    setPostcodeOptions([]);
    if (!selected) return;

    try {
      const cities = await ApiService.getCitiesByState(selected);
      setCityOptions(cities.map((c) => ({ value: c.city, label: c.city })));
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleCityChange = async (selected) => {
    const updated = { ...spForm, sp_city: selected ?? "", sp_postcode: "" };
    setSpForm(updated);
    if (spErrors.sp_city) setSpErrors((e) => ({ ...e, sp_city: "" }));
    if (onSpecialPointChange) onSpecialPointChange(branch.branch_code, updated);

    setPostcodeOptions([]);
    if (!selected) return;

    try {
      const postcodes = await ApiService.getPostcodeByCity(selected);
      setPostcodeOptions(postcodes.map((p) => ({ value: p, label: p })));
    } catch (err) {
      console.error("Error fetching postcodes:", err);
    }
  };

  const specialPointAddress = [
    branch.special_info?.sp_addr,
    branch.special_info?.sp_city,
    branch.special_info?.sp_postcode,
    branch.special_info?.sp_state,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="checkoutbranch_v2">
      <div className="checkoutbranch-content_v2">
        <div className="checkoutbranch-header_v2">
          <div className="checkoutbranch-title-box_v2">
            <div className="checkoutbranch-title_v2">
              <strong>Branch Order Summary</strong>
            </div>
          </div>
          <div className="cbclose-button_v2">
            <button onClick={onClose}>&#10006;</button>
          </div>
        </div>

        <div className="cb-divider_v2"></div>

        <div className="checkoutbranch-content-container_v2">
          {/* ── Special Date and Special Point (salesman only) ── */}
          {hasSalesmanId && (
            <>
              <div className="special-date-section_v2">
                {/* Special Date */}
                <div className="special-date-header_v2">
                  <div className="special-date-title-group_v2">
                    <span className="special-date-title_v2">
                      <LucideCalendar size={18} color="#c0392b" /> Scheduled
                      Delivery Date
                    </span>
                    <span className="special-date-subtitle_v2">
                      Request delivery on a specific date
                    </span>
                  </div>

                  <label className="toggle-switch_v2">
                    <input
                      type="checkbox"
                      className="toggle-slider_v2"
                      checked={isSpecialDateEnabled}
                      onChange={handleToggle}
                    />
                  </label>
                </div>

                {/* Date Picker: label above and input below (Preferred Delivery Date) */}
                {isSpecialDateEnabled && (
                  <>
                    <div className="preferred-row_v2">
                      <div className="preferred-label_v2">
                        Preferred Delivery Date{" "}
                        <span style={{ color: "#c0392b" }}>*</span>
                      </div>
                    </div>

                    <div className="preferred-input-area_v2">
                      <div className="special-date-picker-container_v2 preferred-picker_v2">
                        {/* INPUT */}
                        <input
                          type="text"
                          className="special-date-range-input_v2 preferred-input_v2"
                          readOnly
                          value={
                            specialDate
                              ? new Date(specialDate).toLocaleDateString(
                                  "en-MY",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )
                              : ""
                          }
                          placeholder="dd-----yyyy"
                          onClick={() => setCalendarOpen((o) => !o)}
                        />

                        {/* CLEAR */}
                        {specialDate && (
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={{
                              marginLeft: 8,
                              color: "#aaa",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onSpecialDateChange) {
                                onSpecialDateChange(branch.branch_code, null);
                              }
                            }}
                          />
                        )}

                        {/* ICON */}
                        <LucideCalendar
                          size={16}
                          style={{ cursor: "pointer" }}
                          onClick={() => setCalendarOpen((o) => !o)}
                        />

                        {/* CALENDAR */}
                        {calendarOpen && (
                          <div className="calendar_v2">
                            <Calendar
                              onChange={(date) => {
                                setCalendarOpen(false);

                                if (onSpecialDateChange) {
                                  onSpecialDateChange(branch.branch_code, date);
                                }
                              }}
                              value={specialDate}
                              tileDisabled={({ date }) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                const d = new Date(date);
                                d.setHours(0, 0, 0, 0);

                                return d < today || d.getDay() === 0;
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {hasSpecialPoint && (
              <div className="special-point-section_v2" >
                <div className="special-date-header_v2">
                  <div className="special-date-title-group_v2">
                    <span className="special-date-title_v2">Special Point</span>
                    <span className="special-date-subtitle_v2">
                      Deliver to a different recipient/location
                    </span>
                  </div>

                  <label className="toggle-switch_v2">
                    <input
                      type="checkbox"
                      className="toggle-slider_v2"
                      checked={isSpecialPointEnabled}
                      onChange={handleToggleSpecialPoint}
                    />
                  </label>
                </div>

                {isSpecialPointEnabled && (
                  <div className="sp-form-wrapper_v2">
                    <div className="sp-preferred-row_v2">
                      <div className="preferred-label_v2">
                        Recipient Name{" "}
                        <span style={{ color: "#c0392b" }}>*</span>
                      </div>
                    </div>
                    <div className="sp-input-area_v2">
                      <div className="sp-input-box_v2">
                        <input
                          type="text"
                          className={`sp-input_v2${spErrors.sp_name ? " sp-input-error_v2" : ""}`}
                          placeholder="Full name of recipient"
                          value={spForm.sp_name}
                          onChange={(e) =>
                            handleSpChange("sp_name", e.target.value)
                          }
                        />
                      </div>
                      {spErrors.sp_name && (
                        <span className="sp-error-msg_v2">
                          {spErrors.sp_name}
                        </span>
                      )}
                    </div>

                    <div
                      className="sp-preferred-row_v2"
                      style={{ borderTop: "none" }}
                    >
                      <div className="preferred-label_v2">
                        Delivery Address{" "}
                        <span style={{ color: "#c0392b" }}>*</span>
                      </div>
                    </div>
                    <div className="sp-input-area_v2">
                      <div className="sp-input-box_v2">
                        <input
                          type="text"
                          className={`sp-input_v2${spErrors.sp_addr ? " sp-input-error_v2" : ""}`}
                          placeholder="Street address, building name, unit number"
                          value={spForm.sp_addr}
                          onChange={(e) =>
                            handleSpChange("sp_addr", e.target.value)
                          }
                        />
                      </div>
                      {spErrors.sp_addr && (
                        <span className="sp-error-msg_v2">
                          {spErrors.sp_addr}
                        </span>
                      )}
                    </div>

                    <div className="sp-grid_v2">
                      <div className="sp-col_v2">
                        <div className="preferred-label_v2">
                          State <span style={{ color: "#c0392b" }}>*</span>
                        </div>
                        <CustomDropdown
                          options={stateOptions}
                          value={spForm.sp_state}
                          onChange={handleStateChange}
                          className={`sp-input_v2${spErrors.sp_state ? " sp-input-error_v2" : ""}`}
                          placeholder="Select State"
                          popupRef={modalRef}
                        />
                        {spErrors.sp_state && (
                          <span className="sp-error-msg_v2">
                            {spErrors.sp_state}
                          </span>
                        )}
                      </div>

                      <div className="sp-col_v2">
                        <div className="preferred-label_v2">
                          City <span style={{ color: "#c0392b" }}>*</span>
                        </div>
                        <CustomDropdown
                          options={cityOptions}
                          value={spForm.sp_city}
                          onChange={handleCityChange}
                          className={`sp-input_v2${spErrors.sp_city ? " sp-input-error_v2" : ""}`}
                          placeholder={"Select City"}
                          popupRef={modalRef}
                        />
                        {spErrors.sp_city && (
                          <span className="sp-error-msg_v2">
                            {spErrors.sp_city}
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className="sp-grid_v2"
                      style={{ padding: "0 16px 16px 16px" }}
                    >
                      <div className="sp-col_v2">
                        <div className="preferred-label_v2">
                          Postcode <span style={{ color: "#c0392b" }}>*</span>
                        </div>
                        <CustomDropdown
                          options={postcodeOptions}
                          value={spForm.sp_postcode}
                          onChange={(selected) =>
                            handleSpChange("sp_postcode", selected ?? "")
                          }
                          className={`sp-input_v2${spErrors.sp_postcode ? " sp-input-error_v2" : ""}`}
                          placeholder={"Select Postcode"}
                          popupRef={modalRef}
                        />
                        {spErrors.sp_postcode && (
                          <span className="sp-error-msg_v2">
                            {spErrors.sp_postcode}
                          </span>
                        )}
                      </div>

                      <div className="sp-col_v2">
                        <div className="preferred-label_v2">
                          Contact Number{" "}
                          <span style={{ color: "#c0392b" }}>*</span>
                        </div>
                        <div className="sp-input-box_v2">
                          <input
                            type="text"
                            className={`sp-input_v2${spErrors.sp_phone ? " sp-input-error_v2" : ""}`}
                            placeholder="e.g. 012-3456789"
                            value={spForm.sp_phone}
                            onChange={(e) =>
                              handleSpChange("sp_phone", e.target.value)
                            }
                          />
                        </div>
                        {spErrors.sp_phone && (
                          <span className="sp-error-msg_v2">
                            {spErrors.sp_phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </>
          )}
          {/* ── End Special Date and Special Point ── */}

          {/* show Special Date and Special Point */}
          {(branch?.special_info?.special_point === "1" ||
            branch?.special_info?.special_date === "1") && (
            <div className="branchadd-container_v2">
              {branch?.special_info?.special_point === "1" && (
                <>
                  <div className="branch-pin_v2">
                    <LucideMapPin size={24} color="#7D9274" />
                  </div>
                  <div className="branchadd_v2">
                    <div className="branchadd-title_v2">Special Point</div>
                    <div className="branchadd-address_v2">
                      {specialPointAddress}
                    </div>
                  </div>
                </>
              )}
              {branch?.special_info?.special_date === "1" && (
                <div className="branchdate_v2">
                  <div className="branchdate-title_v2">Special Date</div>
                  <div className="branchdate-date_v2">
                    {branch?.special_info?.special_date_info}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="branchadd-container_v2">
            <div className="branch-pin_v2">
              <LucideMapPin size={24} color="#7D9274" />
            </div>
            <div className="branchadd_v2">
              <div className="branchadd-title_v2">{branch.branch_code}</div>
              <div className="branchadd-address_v2">
                {branch.branch_address}
              </div>
            </div>
          </div>

          <div
            key={`branch-${branch.branch_code}`}
            className="checkout-item_v2"
          >
            <table className="checkout-table_v2">
              <colgroup>
                <col style={{ width: "10%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead className="checkoutitem-header_v2">
                <tr>
                  <th>Image</th>
                  <th>Item Details</th>
                  <th>Quantity</th>
                  <th>Unit Price (RM)</th>
                  <th>Volume (CBM)</th>
                  <th>Amount (RM)</th>
                </tr>
              </thead>
              <tbody>
                {branch.items.map((item) => (
                  <tr key={`item-${item.id}`} className="checkoutitem-row_v2">
                    <td className="checkoutitem-row-image_v2">
                      <FallbackImg
                        src={item.img?.thumb || item.item_photo}
                        alt={item.item_name}
                        style={{ width: "64px", height: "64px" }}
                      />
                    </td>
                    <td>
                      <div
                        style={{
                          color: "#6b7280",
                          fontSize: 12,
                          marginBottom: 4,
                          lineHeight: "1.5",
                        }}
                      >
                        {item.item_code}
                      </div>
                      <div style={{ fontWeight: 400, lineHeight: "1.5" }}>
                        {item.item_name}
                      </div>
                    </td>
                    <td style={{ textAlign: "right" }}>{item.quantity}</td>
                    <td style={{ textAlign: "right" }}>
                      {item.price ? formatCurrency(item.price) : "0.00"}
                    </td>
                    <td>{item.cbm ? Number(item.cbm).toFixed(4) : "NA"}</td>
                    <td style={{ textAlign: "right" }}>
                      {item.subtotal
                        ? formatCurrency(item.subtotal)
                        : item.total
                          ? formatCurrency(item.total)
                          : "0.00"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="checkout-totals-container_v2">
            <div className="checkoutitem-totals-wrapper_v2">
              {(() => {
                const totalCBM = branch.items.reduce(
                  (sum, item) => sum + (Number(item.cbm) || 0),
                  0,
                );
                return (
                  <div className="totals-row_v2">
                    <div className="cbitem-subtitle_v2">Total Volume (CBM)</div>
                    <div className="cbitem-right_v2">
                      <span className="cbitem-colon_v2">:</span>
                      <div className="cbitem-value_v2">
                        {totalCBM.toFixed(4)}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {branch.shipping_fee !== undefined &&
                branch.shipping_fee !== null && (
                  <div className="totals-row_v2">
                    <div className="cbitem-subtitle_v2">Delivery Charges</div>
                    <div className="cbitem-right_v2">
                      <span className="cbitem-colon_v2">:</span>
                      <div className="cbitem-value_v2">
                        RM {formatCurrency(branch.shipping_fee)}
                      </div>
                    </div>
                  </div>
                )}

              {checkoutData &&
                (() => {
                  const branch_shipping_amount = Array.isArray(
                    checkoutData.branch_shipping_detail,
                  )
                    ? checkoutData.branch_shipping_detail.find(
                        (d) => d.branch_code === branch.branch_code,
                      )?.shipping_fee
                    : "0.00";
                  const totalSubtotal = Array.isArray(
                    checkoutData.branch_shipping_detail,
                  )
                    ? checkoutData.branch_shipping_detail.find(
                        (d) => d.branch_code === branch.branch_code,
                      )?.subtotal
                    : "0.00";

                  return (
                    <>
                      <div className="totals-row_v2">
                        <div className="cbitem-subtitle_v2">
                          Delivery Charges
                        </div>
                        <div className="cbitem-right_v2">
                          <span className="cbitem-colon_v2">:</span>
                          <div className="cbitem-value_v2">
                            RM {formatCurrency(branch_shipping_amount)}
                          </div>
                        </div>
                      </div>

                      <div className="totals-row_v2 grand-total-row_v2">
                        <div
                          style={{ fontWeight: 700 }}
                          className="cbitem-subtitle_v2"
                        >
                          Grand Total
                        </div>
                        <div className="cbitem-right_v2">
                          <span className="cbitem-colon_v2">:</span>
                          <div className="cbitem-value_v2">
                            RM {formatCurrency(totalSubtotal)}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutBranch;
